import {
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { WebsiteData } from '../../../../shared/models/website-overview.model';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { AsyncPipe } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { StatusLabelComponent } from '../../../../shared/components/status-label/status-label.component';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  filter,
  map,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { OriginVhostService } from '../../services/origin-vhost.service';
import { OriginVhostStatus } from '../../models/origin-vhost.models';
import { Status } from '../../../../shared/models/status-label.model';
import { StatusModalService } from '../../../../core/services/status-modal.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { WebsocketService } from '../../../../core/services/websocket.service';
import { MatDialog } from '@angular/material/dialog';
import { EditAliasesDialogComponent } from '../../dialogs/edit-aliases-dialog/edit-aliases-dialog.component';
import { MatMenuModule } from '@angular/material/menu';
import { ConfirmationDialogComponent } from '../../../../shared/dialogs/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-origin-vhost',
  imports: [
    MatCardModule,
    AsyncPipe,
    StatusLabelComponent,
    ClipboardModule,
    FontAwesomeModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatMenuModule,
  ],
  templateUrl: './origin-vhost.component.html',
  styleUrl: './origin-vhost.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OriginVhostComponent implements OnInit, OnDestroy {
  private originVhostService = inject(OriginVhostService);

  private statusModalService = inject(StatusModalService);

  private websocketService = inject(WebsocketService);

  private dialog = inject(MatDialog);

  websiteData = input.required<WebsiteData>();

  website = computed(() => this.websiteData().website);

  server = computed(() => this.websiteData().server);

  systemUser = computed(() => this.websiteData().systemUser);

  destroyed$ = new Subject();

  website$ = toObservable(this.website);

  refresh$ = new BehaviorSubject<void>(undefined);

  originVhost$ = this.refresh$.pipe(
    switchMap(() => this.website$),
    switchMap((website) =>
      this.originVhostService.getVhostByWebsiteId(website.id).pipe(
        catchError((error) => {
          if (error.status !== 404) throw error;
          this.status.set(Status.DEACTIVATED);
          return EMPTY;
        }),
      ),
    ),
    shareReplay(1),
  );

  originVhostId = signal(0);

  status = signal<Status>(Status.DEACTIVATED);

  aliases$ = this.originVhost$.pipe(
    map((origin) =>
      origin.aliases.reduce(
        (acc, curr) => acc + `${acc.length ? ', ' : ''}` + curr.alias.replace(/^www\./, ''),
        '',
      ),
    ),
  );

  vhostEnabled = signal(false);

  loadingVhost = signal(false);

  wildcardEnabled = signal(false);

  loadingWildcard = signal(true);

  ngOnInit(): void {
    this.originVhost$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (data) => {
        this.originVhostId.set(data.id);
        this.status.set(this.toStatus(data.status));
        if (data.status === OriginVhostStatus.DONE) {
          this.vhostEnabled.set(true);
          this.loadingWildcard.set(false);
        }
        for (const alias of data.aliases) {
          if (alias.alias.startsWith('*')) {
            this.wildcardEnabled.set(true);
            break;
          }
        }
      },
      error: (error) => {
        this.statusModalService.open({
          state: 'error',
          message: error.error?.error,
        });
      },
    });

    this.websocketService
      .listen<{ title: string }>('notification')
      .pipe(
        takeUntil(this.destroyed$),
        filter(({ title }) => title === 'WildCard Created'),
      )
      .subscribe(() => {
        this.wildcardEnabled.set(true);
        this.loadingWildcard.set(false);
        this.refresh$.next();
      });

    this.websocketService
      .listen<{ title: string }>('notification')
      .pipe(
        takeUntil(this.destroyed$),
        filter(({ title }) => title === 'WildCard Removed'),
      )
      .subscribe(() => {
        this.wildcardEnabled.set(false);
        this.loadingWildcard.set(false);
        this.refresh$.next();
      });

    this.websocketService
      .listen<{ title: string }>('notification')
      .pipe(
        takeUntil(this.destroyed$),
        filter(({ title }) => title === 'Origin Vhost Created!'),
      )
      .subscribe(() => {
        this.vhostEnabled.set(true);
        this.loadingVhost.set(false);
        this.loadingWildcard.set(false);
        this.refresh$.next();
      });

    this.websocketService
      .listen<{ title: string }>('notification')
      .pipe(
        takeUntil(this.destroyed$),
        filter(({ title }) => title === 'Origin Vhost Deleted!'),
      )
      .subscribe(() => {
        this.vhostEnabled.set(false);
        this.loadingVhost.set(false);
        this.wildcardEnabled.set(false);
        this.loadingWildcard.set(true);
        this.refresh$.next();
      });
  }

  onVhostToggleChange(event: MatSlideToggleChange): void {
    event.source.toggle(); // To prevent visual toggle
    if (this.vhostEnabled()) this.deleteVhost();
    else this.createVhost();
  }

  createVhost(): void {
    const server = this.server()?.id;
    const systemUserId = this.systemUser()?.id;
    if (server && systemUserId) {
      this.loadingVhost.set(true);
      this.originVhostService
        .createVhost({
          server,
          websiteId: this.website().id,
          systemUserId,
        })
        .subscribe({
          error: (error) => {
            this.statusModalService.open({
              state: 'error',
              message: error.error?.error,
            });
          },
        });
    }
  }

  deleteVhost() {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete Origin VHost',
          paragraphs: [
            `Deleting Origin Vhost will remove the website from VMAX.`,
            `The website will go offline.`,
            `Are you sure you want to delete Origin Vhost for ${this.website().domain}? `,
          ],
          primaryButtonText: 'Delete Origin VHost',
          type: 'delete',
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter(Boolean),
        switchMap(() => this.originVhost$.pipe(take(1))),
        tap(() => this.loadingVhost.set(true)),
        switchMap((origin) => this.originVhostService.deleteVhost(origin.id)),
      )
      .subscribe({
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
      });
  }

  onWildcardToggleChange(event: MatSlideToggleChange): void {
    event.source.toggle(); // To prevent visual toggle
    if (this.wildcardEnabled()) this.removeWildCard();
    else this.addWildCard();
  }

  addWildCard(): void {
    this.originVhost$
      .pipe(
        take(1),
        tap(() => this.loadingWildcard.set(true)),
        switchMap((origin) => this.originVhostService.createWildCard(origin.id)),
      )
      .subscribe({
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
      });
  }

  removeWildCard(): void {
    this.originVhost$
      .pipe(
        take(1),
        tap(() => this.loadingWildcard.set(true)),
        switchMap((origin) => this.originVhostService.removeWildCard(origin.id)),
      )
      .subscribe({
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
      });
  }

  editAliases(): void {
    this.originVhost$
      .pipe(
        take(1),
        switchMap((origin) =>
          this.dialog
            .open(EditAliasesDialogComponent, {
              data: {
                subTitle: this.website().domain,
                aliases: origin.aliases.map((a) => a.alias),
                php: origin.php,
              },
            })
            .afterClosed()
            .pipe(
              filter((x) => !!x),
              switchMap((data) => this.originVhostService.updateVhost(origin.id, data)),
            ),
        ),
      )
      .subscribe({
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
      });
  }

  toStatus(key: OriginVhostStatus): Status {
    switch (key) {
      case OriginVhostStatus.DONE:
        return Status.ACTIVE;
      case OriginVhostStatus.IN_PROGRESS:
        return Status.IN_PROGRESS;
      default:
        return Status.ERROR;
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next('');
    this.destroyed$.complete();
  }
}
